import { LegalPage } from "./LegalPage";

export const TermsConditions = () => {
  return (
    <LegalPage title="Terms of Use" lastUpdated="June 4, 2026">
      <p className="legalUpdated">Effective as of June 4, 2026</p>

      <p>
        <strong>
          PLEASE READ THESE TERMS OF USE CAREFULLY BEFORE USING THE APPLICATION
        </strong>
      </p>

      <h2>Contents</h2>
      <ul>
        <li>Interpretation and Definitions</li>
        <li>Introduction</li>
        <li>About Sharecipe</li>
        <li>Using Sharecipe</li>
        <li>Account Registration</li>
        <li>Registration as an Enthusiast</li>
        <li>Registration as a Vendor</li>
        <li>Deleting Your Account</li>
        <li>Content</li>
        <li>Alcohol-Related Content</li>
        <li>Liabilities</li>
        <li>Allergy Management</li>
        <li>Marketplace</li>
        <li>Responsibility for the Content</li>
        <li>Limitation of Liability</li>
        <li>Subscription and Payments</li>
        <li>Third-Party Charges</li>
        <li>Changes to Subscription Fees</li>
        <li>Cancellation of Subscription</li>
        <li>Contacting Us</li>
      </ul>

      <h2>Interpretation and Definitions</h2>
      <h3>Interpretation</h3>
      <p>
        Words with initial capital letters have meanings defined below. The
        following definitions have the same meaning whether they appear in
        singular or plural.
      </p>

      <h3>Definitions</h3>
      <p>For the purpose of these Terms of Use:</p>
      <ul>
        <li>
          <strong>"Sharecipe" or "Application"</strong> means the software
          program provided by the Company and downloaded by You on an electronic
          device, named Sharecipe.
        </li>
        <li>
          <strong>"Company", "we", "us", or "our"</strong> refers to AZ
          Developers LLC, the entity that owns and operates the Application and
          enters into a contract with You.
        </li>
        <li>
          <strong>"User" or "You"</strong> means an individual or entity that has
          downloaded the Application from an Application Store and/or has
          registered to use all or part of the services provided in the
          Application.
        </li>
        <li>
          <strong>"Application Store"</strong> means Apple App Store, Google Play
          Store, or another digital distribution service through which the
          Application is downloaded.
        </li>
        <li>
          <strong>"Enthusiast"</strong> means a registered user with whole or
          partial access to the features of the Application.
        </li>
        <li>
          <strong>"Vendor"</strong> means a registered user who has access to the
          Marketplace to list food-related products or services for other Users
          to view or consider.
        </li>
        <li>
          <strong>"Product"</strong> means goods or services displayed by Vendors
          in marketplace listings.
        </li>
        <li>
          <strong>"Free Account"</strong> means a free account with basic
          features.
        </li>
        <li>
          <strong>"Premium Account"</strong> means a subscription-based account
          with additional features.
        </li>
        <li>
          <strong>"Vendor Account"</strong> means a subscription-based account of
          a registered Vendor.
        </li>
        <li>
          <strong>"Personal Page"</strong> means the personal account area of a
          Vendor for leisure use of the Application.
        </li>
        <li>
          <strong>"Shop Page"</strong> means the account area allowing a Vendor to
          manage marketplace listings.
        </li>
        <li>
          <strong>"Marketplace"</strong> means the listing-only feature where
          Vendors may display food-related products or services and Users may
          discover or contact Vendors.
        </li>
        <li>
          <strong>"Content"</strong> means information in the Application,
          including but not limited to recipes, articles, products, comments,
          responses, photographs, listings, ingredients, and other materials
          submitted by Users.
        </li>
        <li>
          <strong>"Recipe"</strong> means a recipe submitted by a User and made
          available through the Application.
        </li>
        <li>
          <strong>"Club Recipe"</strong> means a recipe published within a Club by
          a Club member.
        </li>
        <li>
          <strong>"Article"</strong> means an article published by a User within a
          Club or community feature.
        </li>
        <li>
          <strong>"Publication"</strong> means Content submitted by a User,
          including recipes, Club recipes, and articles.
        </li>
        <li>
          <strong>"Ranks" or "Ranking System"</strong> means ranks given to Users
          according to the number of Publications submitted in the Application.
        </li>
      </ul>

      <h2>Introduction</h2>
      <p>
        These Terms of Use and all additional terms posted on or in the
        Application set out the terms on which You may use the Application. The
        Application is owned and operated by AZ Developers LLC, registered at 21
        Alimardan Topchubashov str., apt 82, Nasimi region, Baku AZ1009,
        Azerbaijan.
      </p>
      <p>
        By using the Application, You confirm that You accept and agree to comply
        with these Terms of Use. If You do not agree to these Terms of Use, You
        must not use the Application. We recommend that You keep a copy of these
        Terms of Use for future reference.
      </p>

      <h2>About Sharecipe</h2>
      <p>
        Sharecipe is a multifunctional social platform that allows Users to
        search, discuss, and share recipes and culinary experiences. The
        Application provides features to help Users manage ingredients, diets,
        allergies, cuisines, recipes, clubs, profiles, and related culinary
        content.
      </p>
      <p>
        The Application also provides a listing-only marketplace where Vendors may
        display food-related products or services and Users may discover or
        contact Vendors in their geographic area. Sharecipe is not a seller,
        buyer, broker, agent, food provider, delivery provider, payment processor
        for marketplace transactions, or party to any transaction between Users
        and Vendors.
      </p>
      <p>
        Sharecipe does not verify Vendors, inspect food, check kitchens, control
        payments, arrange delivery, guarantee quality or safety, or guarantee that
        any User or Vendor will complete a transaction. All marketplace
        transactions, communications, payments, deliveries, refunds, complaints,
        food safety issues, and disputes are solely between the relevant User and
        Vendor.
      </p>

      <h2>Using Sharecipe</h2>
      <p>
        Subject to Your compliance with these Terms of Use, the Company grants You
        a non-exclusive, non-transferable, single-user license to download and
        install one copy of the Sharecipe mobile application on Your mobile device
        and to access and use the Application for Your own personal or permitted
        business use, depending on Your account type.
      </p>
      <p>You will not:</p>
      <ul>
        <li>
          copy, modify, or distribute the Application for any unauthorized
          purpose;
        </li>
        <li>
          transfer, sublicense, lease, lend, rent, or otherwise distribute the
          Application to any third party;
        </li>
        <li>
          decompile, reverse-engineer, disassemble, or create derivative works of
          the Application except where such restriction is prohibited by law;
        </li>
        <li>
          make the functionality of the Application available to multiple users
          through any unauthorized means;
        </li>
        <li>
          post false, inaccurate, misleading, deceptive, defamatory, libelous,
          unsafe, harmful, or illegal content;
        </li>
        <li>
          distribute or post spam, unsolicited or bulk electronic communications,
          chain letters, or pyramid schemes;
        </li>
        <li>
          distribute viruses, malware, or any technologies that may harm the
          Application, the Company, or Users;
        </li>
        <li>
          harvest, scrape, or otherwise collect or use information about Users
          without their consent;
        </li>
        <li>
          use robots, spiders, scrapers, data mining tools, data gathering tools,
          or other automated means to access the Application for any unauthorized
          purpose;
        </li>
        <li>circumvent any technical measures provided in the Application;</li>
        <li>
          use the Application in any unlawful manner, for any unlawful purpose, or
          in any manner inconsistent with these Terms of Use.
        </li>
      </ul>
      <p>
        If we believe You are violating these Terms of Use or abusing the
        Application, we may, in our sole discretion and without limiting other
        remedies, limit, suspend, or terminate Your account or access, delay or
        remove hosted Content, remove or demote listings, remove special status
        associated with an account, and take technical or legal steps to protect
        the Application and its Users.
      </p>

      <h2>Account Registration</h2>
      <p>
        In order to use certain features of the Application, You may be required
        to register as an Enthusiast or a Vendor. You may be asked to provide
        account information in the format specified by us, or You may be able to
        sign up through a third-party account, such as Google, Facebook, or Apple,
        if supported.
      </p>
      <p>
        You are responsible for providing accurate information, keeping Your login
        credentials secure, and all activity that occurs under Your account.
      </p>

      <h2>Registration as an Enthusiast</h2>
      <p>
        Once registered as an Enthusiast, You may choose a Free Account or a
        subscription-based Premium Account. Premium Account features may include
        additional filters, wider selections of cuisines and meal types, allergy
        filtering options, and club interaction features. Actual features may vary
        by app version, country, platform, and account status.
      </p>

      <h3>Cuisine Filter</h3>
      <div className="legalTableWrap">
        <table className="legalTable">
          <thead>
            <tr>
              <th>Free Account</th>
              <th>Premium Account</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <ul>
                  <li>European Cuisine</li>
                  <li>North American Cuisine</li>
                  <li>South American Cuisine</li>
                  <li>African Cuisine</li>
                  <li>Middle Eastern Cuisine</li>
                  <li>Asian Cuisine</li>
                  <li>Oceanic Cuisine</li>
                  <li>My own invention</li>
                  <li>Homemade recipes</li>
                </ul>
              </td>
              <td>
                <ul>
                  <li>European Cuisine</li>
                  <li>North American Cuisine</li>
                  <li>South American Cuisine</li>
                  <li>African Cuisine</li>
                  <li>Middle Eastern Cuisine</li>
                  <li>Mediterranean Cuisine</li>
                  <li>Asian Cuisine</li>
                  <li>Oceanic Cuisine</li>
                  <li>Fusion Cuisine</li>
                  <li>Haute Cuisine</li>
                  <li>My own invention</li>
                  <li>Homemade recipes</li>
                  <li>Cuisine of a country selected by the User</li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3>Meal Types</h3>
      <div className="legalTableWrap">
        <table className="legalTable">
          <thead>
            <tr>
              <th>Free Account</th>
              <th>Premium Account</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <ul>
                  <li>Breakfast</li>
                  <li>Salads</li>
                  <li>Sandwiches</li>
                  <li>Snacks</li>
                  <li>Sauces &amp; Seasonings</li>
                  <li>Soups</li>
                  <li>Meals</li>
                  <li>Desserts</li>
                  <li>Drinks</li>
                  <li>Other</li>
                </ul>
              </td>
              <td>
                <ul>
                  <li>Breakfast</li>
                  <li>Salads</li>
                  <li>Sandwiches</li>
                  <li>Snacks</li>
                  <li>Sauces &amp; Seasonings</li>
                  <li>Soups</li>
                  <li>Meals</li>
                  <li>Desserts</li>
                  <li>Hot drinks</li>
                  <li>Non-alcohol drinks</li>
                  <li>Alcohol drinks</li>
                  <li>Wine snacks</li>
                  <li>Beer snacks</li>
                  <li>Hard liquor snacks</li>
                  <li>Cocktail snacks</li>
                  <li>Amaze your guests!</li>
                  <li>Other</li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3>Allergies Filter</h3>
      <div className="legalTableWrap">
        <table className="legalTable">
          <thead>
            <tr>
              <th>Free Account</th>
              <th>Premium Account</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <p>Top 10 allergies list:</p>
                <ol>
                  <li>Dairy</li>
                  <li>Egg</li>
                  <li>Gluten</li>
                  <li>Peanut</li>
                  <li>Shellfish</li>
                  <li>Fish</li>
                  <li>Sesame</li>
                  <li>Soy</li>
                  <li>Sulfite</li>
                  <li>Tree nut</li>
                </ol>
              </td>
              <td>
                <p>Top 10 allergies list:</p>
                <ol>
                  <li>Dairy</li>
                  <li>Egg</li>
                  <li>Gluten</li>
                  <li>Peanut</li>
                  <li>Shellfish</li>
                  <li>Fish</li>
                  <li>Sesame</li>
                  <li>Soy</li>
                  <li>Sulfite</li>
                  <li>Tree nut</li>
                </ol>
                <p>
                  Additional allergy or ingredient filtering options selected by
                  the User, where available.
                </p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3>Clubs</h3>
      <div className="legalTableWrap">
        <table className="legalTable">
          <thead>
            <tr>
              <th>Free Account</th>
              <th>Premium Account</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                Browsing features according to the account status and app version.
              </td>
              <td>
                Create or join a Club and interact with recipes and articles in
                Clubs through comments, likes, and other available features.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3>Ranking System</h3>
      <p>
        The Ranking System allows Users to unlock certain features by submitting
        Publications. Both Free Account and Premium Account Users may start at the
        lowest Rank. Premium subscribers may have Premium features available while
        the subscription remains active. Specific ranking rules and unlocked
        features may vary by app version.
      </p>
      <div className="legalTableWrap">
        <table className="legalTable">
          <thead>
            <tr>
              <th>Ranking</th>
              <th>Chef's hat color</th>
              <th>Number of Publications</th>
              <th>Features unlocked at promotion</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Rookie</td>
              <td>White</td>
              <td>0</td>
              <td>Basic features</td>
            </tr>
            <tr>
              <td>Waiter</td>
              <td>Yellow</td>
              <td>10</td>
              <td>Comments/likes to recipes</td>
            </tr>
            <tr>
              <td>Dishwasher</td>
              <td>Orange</td>
              <td>30</td>
              <td>Comments/likes within Clubs</td>
            </tr>
            <tr>
              <td>Kitchen porter</td>
              <td>Green</td>
              <td>50</td>
              <td>Premium Meal Types filter</td>
            </tr>
            <tr>
              <td>Junior chef</td>
              <td>Blue</td>
              <td>100</td>
              <td>Join a Club, publish recipes/articles within Clubs</td>
            </tr>
            <tr>
              <td>Station chef</td>
              <td>Purple</td>
              <td>150</td>
              <td>Premium Cuisine filter excluding country</td>
            </tr>
            <tr>
              <td>Deputy chef</td>
              <td>Red</td>
              <td>200</td>
              <td>Creating Clubs</td>
            </tr>
            <tr>
              <td>Head chef</td>
              <td>Brown</td>
              <td>300</td>
              <td>Premium Cuisine filter including country</td>
            </tr>
            <tr>
              <td>Executive chef</td>
              <td>Black</td>
              <td>365</td>
              <td>Specific allergy filter</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p>
        A Premium Account may renew automatically unless cancelled according to
        the Subscription and Payments section below. Any free or discounted
        Premium Account status based on ranking is subject to the rules displayed
        in the Application and may be changed as the Application develops.
      </p>

      <h2>Registration as a Vendor</h2>
      <p>
        A Vendor Account may be based on a monthly subscription fee. The Vendor
        Account allows Vendors to create and manage marketplace listings so that
        Users in a relevant geographic area may view or contact the Vendor about
        food-related products or services.
      </p>
      <p>
        Sharecipe provides only the listing space. Sharecipe does not process
        marketplace payments, arrange delivery, verify Vendors, inspect food,
        check kitchens, confirm permits, guarantee product quality, or participate
        in transactions between Vendors and Users.
      </p>
      <p>
        Besides marketplace listing features, a Vendor Account may include access
        to certain Premium Account features for personal use. The Vendor Account
        may include a Personal Page and a Shop Page. The Shop Page allows the
        Vendor to manage listings, while the Personal Page allows the Vendor to
        use available personal features of the Application.
      </p>
      <p>
        If You subscribe to the Vendor Account, subscription fees may be payable on
        a recurring basis and may automatically renew unless cancelled according to
        the Subscription and Payments section below.
      </p>

      <h2>Deleting Your Account</h2>
      <p>
        If You want to delete Your Sharecipe account, You may be required to cancel
        any active subscription before account deletion can be completed. Deleting
        the Sharecipe Application from Your device does not automatically cancel
        Your subscription or delete Your account. You must cancel Your subscription
        through the relevant application store, payment processor, subscription
        management page, or account settings, depending on how You subscribed.
      </p>

      <h2>Content</h2>
      <p>
        You keep ownership of the recipes, photographs, comments, articles,
        listings, and other Content You upload or publish in Sharecipe.
      </p>
      <p>
        By uploading, posting, submitting, or publishing Content in the
        Application, You grant Sharecipe a worldwide, non-exclusive, royalty-free
        license to host, store, reproduce, display, publish, translate, adapt,
        format, distribute, and otherwise use that Content only as reasonably
        necessary to operate, provide, maintain, improve, promote, and protect the
        Application and its services.
      </p>
      <p>This license allows Sharecipe to:</p>
      <ul>
        <li>
          display Your Content to other Users according to the features of the
          Application;
        </li>
        <li>store and back up Your Content;</li>
        <li>
          technically modify, resize, format, translate, or adapt Your Content so
          it works properly in the Application;
        </li>
        <li>share Your Content within the Application and related Sharecipe services;</li>
        <li>
          use screenshots, previews, or examples of public Content for reasonable
          promotional purposes connected to Sharecipe, unless prohibited by
          applicable law or Your account settings.
        </li>
      </ul>
      <p>This license does not transfer ownership of Your Content to Sharecipe.</p>
      <p>
        You confirm that You have the necessary rights to upload and publish Your
        Content and that Your Content does not violate the rights of any third
        party or any applicable law.
      </p>
      <p>
        When You delete Content or close Your account, Sharecipe will remove or
        anonymize the Content where reasonably possible, subject to technical
        limitations, backups, legal obligations, moderation records, safety
        requirements, and copies already shared or displayed before deletion.
      </p>

      <h2>Alcohol-Related Content</h2>
      <p>
        Sharecipe may include recipes, meal categories, or user content that
        references alcoholic drinks or ingredients. Such content is provided for
        culinary information only and must not be used in violation of applicable
        age restrictions, alcohol laws, advertising rules, or local regulations.
      </p>
      <p>
        Vendors are not allowed to list, sell, deliver, or promote alcoholic
        products through the Marketplace unless Sharecipe expressly allows this in
        writing and the Vendor fully complies with all applicable laws, licensing
        requirements, age-verification rules, and platform policies. Unless such
        express permission is given, alcohol product listings are prohibited.
      </p>
      <p>
        Sharecipe may remove alcohol-related listings or Content where required by
        law, platform rules, user safety, or these Terms.
      </p>

      <h2>Liabilities</h2>

      <h3>Allergy Management</h3>
      <p>
        The Application provides an allergy filtering feature for Users'
        convenience. This feature should not be treated as medical advice, health
        advice, or a guarantee of safety.
      </p>
      <p>
        The allergy filtering feature works by comparing ingredients selected from
        Sharecipe's predefined ingredient database against allergy categories
        selected by the User. Users cannot freely write their own ingredients for
        this filtering system; instead, they select ingredients from the available
        list. Certain ingredients in the database may be linked to one or more
        allergy categories.
      </p>
      <p>
        The database may identify ingredients that do or may contain allergens, but
        it cannot detect every possible allergen, cross-contact risk, hidden
        ingredient, substitution, labeling change, user mistake, or Vendor mistake.
      </p>
      <p>
        Although Sharecipe aims to make the allergy database useful and
        comprehensive, food allergies are complex and ingredient data may be
        incomplete, inaccurate, outdated, differently named, translated
        incorrectly, or affected by preparation methods, substitutions, hidden
        ingredients, cross-contact, contamination, packaging changes, or user
        error.
      </p>
      <p>
        Sharecipe does not guarantee that the allergy filtering feature will
        identify every allergen or prevent allergic reactions. Recipes and
        marketplace listings are created or submitted by Users and Vendors, and
        Sharecipe does not independently verify every ingredient, recipe, product,
        listing, preparation method, or allergen warning.
      </p>
      <p>
        Before preparing, consuming, purchasing, or serving any food based on
        information in the Application, Users must:
      </p>
      <ul>
        <li>carefully review all ingredients;</li>
        <li>
          check product labels, packaging, sauces, condiments, ready-to-use
          products, and substituted ingredients;
        </li>
        <li>consider possible hidden allergens and cross-contact risks;</li>
        <li>
          consult a qualified medical professional or allergist if they have
          serious allergies or are unsure about any ingredient;
        </li>
        <li>
          avoid relying only on Sharecipe's allergy filtering feature for health or
          safety decisions.
        </li>
      </ul>
      <p>
        <strong>
          IMPORTANT: Sharecipe's allergy filtering feature is provided for
          convenience only. It is not a medical tool and does not guarantee that
          recipes, ingredients, products, or marketplace listings are allergen-free
          or safe for any User. To the maximum extent permitted by applicable law,
          Sharecipe and the Company are not liable for allergic reactions, health
          issues, injury, illness, loss, or damage arising from a User's reliance
          on recipes, ingredients, products, marketplace listings, Vendor
          information, or the allergy filtering feature.
        </strong>
      </p>

      <h3>Marketplace</h3>
      <p>
        Sharecipe provides a listing-only marketplace where Vendors may display
        food-related products or services and where Users may view, contact, or
        choose Vendors. Sharecipe is not a seller, buyer, broker, agent, food
        provider, delivery provider, payment processor for marketplace
        transactions, or party to any transaction between Users and Vendors.
      </p>
      <p>
        Sharecipe does not verify Vendors, inspect products, check kitchens, review
        hygiene standards, confirm licenses or permits, control delivery, handle
        payments between Users and Vendors, guarantee product quality, or guarantee
        that any Vendor or User will complete a transaction.
      </p>
      <p>
        All marketplace transactions, communications, payments, delivery
        arrangements, collection arrangements, refunds, complaints, product quality
        issues, food safety issues, and disputes are solely between the relevant
        User and Vendor.
      </p>
      <p>
        <strong>For Vendors:</strong>
      </p>
      <ul>
        <li>
          You are solely responsible for the accuracy and completeness of Your
          listings.
        </li>
        <li>You are solely responsible for the products or services You offer.</li>
        <li>
          You must comply with all applicable laws, regulations, licensing
          requirements, food safety rules, tax obligations, consumer protection
          rules, allergen disclosure rules, and other legal requirements in Your
          location.
        </li>
        <li>
          You must not list illegal, unsafe, misleading, counterfeit, prohibited,
          or harmful products or services.
        </li>
        <li>
          You must provide accurate information about ingredients, allergens,
          preparation, storage, delivery, pricing, availability, and any conditions
          of sale where applicable.
        </li>
        <li>
          You are solely responsible for handling payments, delivery, refunds,
          cancellations, complaints, and disputes with Users.
        </li>
      </ul>
      <p>
        <strong>For Users:</strong>
      </p>
      <ul>
        <li>
          You are solely responsible for deciding whether to contact, trust,
          purchase from, or transact with any Vendor.
        </li>
        <li>You should make Your own checks before purchasing any product or service.</li>
        <li>
          You should ask Vendors directly about ingredients, allergens, hygiene,
          storage, delivery, licensing, refunds, and other relevant details.
        </li>
        <li>
          Any dispute related to a marketplace transaction must be resolved directly
          between You and the Vendor.
        </li>
      </ul>
      <p>
        Sharecipe may remove listings, suspend accounts, restrict marketplace
        access, or take other action if we believe a listing, Vendor, User, or
        transaction may violate these Terms, applicable law, food safety rules, user
        safety, or the integrity of the Application. However, any such action does
        not mean that Sharecipe verifies or accepts responsibility for Vendors,
        Users, products, services, listings, or transactions.
      </p>

      <h2>Responsibility for the Content</h2>
      <p>
        The Content of the Application is provided for general information only. We
        do not guarantee that Content published by Users is appropriate, correct,
        complete, safe, accurate, or free from unsafe recipes, ingredients,
        preparation methods, cooking methods, allergens, or other risks. The
        Content provided in the Application is not intended to amount to advice on
        which You should rely, and any use of or reliance on any part of the
        Content is at Your own risk.
      </p>
      <p>
        We do not make any statements, whether express or implied, about the
        accuracy, quality, safety, legality, or fitness for purpose of Content
        published in the Application.
      </p>
      <p>Therefore:</p>
      <ul>
        <li>
          You should verify any unusual or novel ingredients or methods in a recipe
          by referring to other reliable sources;
        </li>
        <li>
          You should comply at all times with food safety notices, product recalls,
          warnings, and guidance issued by local government authorities or other
          appropriate bodies;
        </li>
        <li>
          You should not expect Sharecipe to identify or explain all risks contained
          in food preparation, cooking methods, ingredients, storage, consumption,
          or marketplace listings;
        </li>
        <li>
          If You are not an experienced cook, or if a recipe involves unfamiliar
          techniques, ingredients, allergens, preservation, storage, or
          safety-sensitive steps, You should seek guidance from an experienced cook,
          qualified professional, or other appropriate authority.
        </li>
      </ul>
      <p>
        Each User is solely responsible for the Content that he or she posts,
        uploads, or publishes using the Application. You promise that:
      </p>
      <ul>
        <li>any Content You post or publish complies with these Terms of Use;</li>
        <li>
          You have the right to use, upload, post, publish, and license the Content
          to Sharecipe as described in these Terms of Use;
        </li>
        <li>
          Your Content will not infringe the rights of any third party or violate
          applicable law.
        </li>
      </ul>
      <p>You agree and acknowledge that:</p>
      <ul>
        <li>
          we will not be responsible or liable to You or any third party for the
          Content or accuracy of any Content posted by You or any other User;
        </li>
        <li>
          we have the right to remove any Content made available in the Application
          at our sole discretion, including where we believe the Content violates
          these Terms, applicable law, user safety, platform rules, or the rights of
          Sharecipe, Users, or third parties;
        </li>
        <li>You are solely responsible for securing and backing up Your Content.</li>
      </ul>

      <h2>Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by applicable law, Sharecipe, the Company,
        and its subsidiaries, affiliates, officers, employees, agents, partners, and
        licensors will not be liable for any direct, indirect, incidental, special,
        consequential, exemplary, or punitive damages, including without limitation
        loss of profits, data, use, goodwill, personal or bodily injury, emotional
        distress, or other intangible losses resulting from:
      </p>
      <ul>
        <li>
          Your access to, use of, or inability to access or use the Application;
        </li>
        <li>
          any conduct or Content of any third party on the Application, including
          defamatory, offensive, unsafe, illegal, or harmful conduct of other Users,
          Vendors, or third parties;
        </li>
        <li>
          any Content, recipe, ingredient information, allergy filter result,
          marketplace listing, product, service, or information obtained from the
          Application;
        </li>
        <li>
          any communications, interactions, meetings, purchases, deliveries,
          disputes, or other dealings with other Users, Vendors, or persons with
          whom You communicate or interact as a result of Your use of the
          Application;
        </li>
        <li>
          unauthorized access, use, or alteration of Your transmissions or Content.
        </li>
      </ul>
      <p>
        This limitation applies whether based on warranty, contract, tort,
        negligence, strict liability, or any other legal theory, whether or not the
        Company has been informed of the possibility of such damage, and even if a
        remedy is found to have failed of its essential purpose. Some jurisdictions
        do not allow certain limitations of liability, so some of the above
        limitations may not apply to You.
      </p>

      <h2>Subscription and Payments</h2>
      <p>
        Sharecipe may offer free and paid account features, including Premium
        Account and Vendor Account features. Paid features, pricing, billing period,
        renewal terms, cancellation options, and available payment methods may vary
        depending on the platform, country, application store, payment processor,
        and account type.
      </p>
      <p>
        For the iOS version of the Application, payments for digital subscriptions
        and paid app features are processed through Apple's in-app purchase system.
        Apple handles the payment transaction under its own terms, policies, and
        privacy practices.
      </p>
      <p>
        For the Android version of the Application, payments may be processed by
        Stripe or another payment provider permitted for the relevant feature,
        transaction type, platform rules, country, and payment method. If applicable
        platform rules require a particular payment system, Sharecipe may use that
        system.
      </p>
      <p>
        Sharecipe does not collect or store full credit card numbers, debit card
        numbers, CVV codes, or full billing details. Payment information is handled
        by the relevant payment processor or application store. Sharecipe may
        receive limited payment-related information such as transaction identifiers,
        receipts, subscription status, renewal status, cancellation status, and
        payment confirmation, which we use to manage access to paid features and
        provide support.
      </p>
      <p>
        Subscriptions renew automatically unless cancelled before the end of the
        current billing period, according to the rules of the relevant application
        store or payment processor.
      </p>
      <p>
        Deleting the Sharecipe Application from Your device does not automatically
        cancel Your subscription. You must cancel Your subscription through the
        relevant subscription management page, application store, payment processor,
        or account settings, depending on how You subscribed.
      </p>
      <p>
        Unless required by applicable law or the rules of the relevant application
        store or payment processor, subscription fees are non-refundable and there
        are no refunds or credits for partially used billing periods.
      </p>

      <h2>Third-Party Charges</h2>
      <p>
        Neither Sharecipe nor the Application Store is responsible for any charges
        or fees imposed by Your bank, card issuer, mobile carrier, internet
        provider, payment account provider, or other third party in connection with
        subscription payments, failed payments, data usage, currency conversion, or
        other charges.
      </p>
      <p>
        If payment cannot be successfully processed, we or the applicable
        application store or payment processor may suspend or terminate access to
        paid features, subject to applicable law and platform rules.
      </p>

      <h2>Changes to Subscription Fees</h2>
      <p>
        We may increase or decrease subscription fees or change paid features from
        time to time. Where required by applicable law, application store rules, or
        payment processor rules, we will notify You before a price change takes
        effect. If You do not agree to a price change, You may cancel Your
        subscription before the change takes effect. If You do not cancel, You may
        be deemed to have accepted the change according to the applicable
        subscription rules.
      </p>

      <h2>Cancellation of Subscription</h2>
      <p>
        Subscriptions are generally pre-paid and automatically renewed. You can
        cancel Your subscription through the relevant application store, payment
        processor, subscription management page, or account settings, depending on
        how You subscribed. Cancellation will usually take effect at the end of the
        then-current billing period unless applicable law, application store rules,
        or payment processor rules provide otherwise.
      </p>
      <p>
        <strong>
          IMPORTANT: Deleting the Application from Your device does not cancel Your
          subscription. Unless required by applicable law or the rules of the
          relevant application store or payment processor, subscription fees are
          non-refundable and there are no refunds or credits for partial billing
          periods.
        </strong>
      </p>

      <h2>Contacting Us</h2>
      <p>
        If You have any questions related to these Terms of Use or the Sharecipe
        Application, please contact us at{" "}
        <a href="mailto:info@sharecipe.net">info@sharecipe.net</a>.
      </p>
    </LegalPage>
  );
};
